import {
  AfterViewInit,
  Component,
  DoCheck,
  Input,
  IterableChanges,
  IterableDiffer,
  IterableDiffers,
  OnDestroy,
  Optional,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PwLocaleService } from '@pw-utils/services/locale/locale.service';
import { MAPBOX_ACCESS_TOKEN, MAPBOX_STYLE } from '../../core/consts/mapbox.const';
import { IMarker, IPoint } from '../../core/interfaces/marker.interface';
import { MapboxApiService } from '../../services/mapbox-api/mapbox-api.service';

@Component({
  selector: 'pw-simple-mapbox',
  templateUrl: './simple-mapbox.component.html',
  styleUrls: ['./simple-mapbox.component.scss'],
})
export class SimpleMapboxComponent implements AfterViewInit, DoCheck, OnDestroy {
  private _markers: IMarker[];

  @Input()
  public get markers(): IMarker[] {
    return this._markers;
  }

  public set markers(value: IMarker[]) {
    this._markers = value || [];
    this._arrayDiffer = this._differs.find(this.markers).create();

    if (!!this.mapbox) this._updateMarkers();
  }

  private _isScrollZoomEnabled = true;

  @Input()
  public get isScrollZoomEnabled(): boolean {
    return this._isScrollZoomEnabled;
  }

  public set isScrollZoomEnabled(value: boolean) {
    this._isScrollZoomEnabled = value;
    this._applyScrollZoom();
  }

  public mapBoxId = `shipment-mapbox-${Date.now()}`;
  public mapbox: any;
  private mapboxApi: any;
  private _arrayDiffer: IterableDiffer<any>;
  private _currDiffs: IterableChanges<any>;
  private _mapboxMarkers: any[] = [];
  private _wheelEvent: (e) => void;
  private _dragStartEvent: (e) => void;
  private _touchStartEvent: (e) => void;
  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private _differs: IterableDiffers,
    private _mapboxApiService: MapboxApiService,
    @Optional() private _localeService: PwLocaleService
  ) {}

  public ngAfterViewInit(): void {
    this._localeService
      ?.listenLocale()
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((lang) => this.changeMapLanguage(lang));

    this._mapboxApiService
      .getMapboxGlApi()
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((api) => this._initMap(api));
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public ngDoCheck(): void {
    if (!this.mapbox) return;

    this._currDiffs = this._arrayDiffer.diff(this.markers);
    if (!!this._currDiffs) this._updateMarkers();
  }

  public changeMapLanguage(lang: string): void {
    if (!this.mapbox || !lang) return;

    try {
      this.mapbox
        .getStyle()
        .layers.filter((item: { id: string }) => item.id.includes('label'))
        .map((item: { id: string }) => item.id)
        .forEach((layerName: string) => this._setLayerLang(layerName, lang));
    } catch (e) {}
  }

  private _setLayerLang(layerName, lang): void {
    this.mapbox.setLayoutProperty(layerName, 'text-field', ['get', 'name_' + lang]);
  }

  private _initMap(api: any): void {
    this.mapboxApi = api;
    if (!this.mapboxApi) return;

    this.mapboxApi.accessToken = MAPBOX_ACCESS_TOKEN;
    this.mapbox = new this.mapboxApi.Map(this._getMapboxInitConfig())
      .addControl(new this.mapboxApi.NavigationControl())
      .addControl(new this.mapboxApi.AttributionControl({ compact: true }));

    this.mapbox.scrollZoom.setWheelZoomRate(0.02);
    this._initZoomEvents();
    this._applyScrollZoom();

    this._mapboxMarkers = this.markers?.map((marker) => this._addMarkerToMap(marker));
    this.mapbox.on('load', () => this.changeMapLanguage(this._localeService.currentLanguage));
  }

  private _applyScrollZoom(): void {
    if (!this.mapbox) return;

    if (this.isScrollZoomEnabled) {
      this.mapbox.dragPan.enable();
      this.mapbox.scrollZoom.enable();
      this.mapbox.off('wheel', this._wheelEvent);
      this.mapbox.off('dragstart', this._dragStartEvent);
      this.mapbox.off('touchstart', this._touchStartEvent);
    } else {
      this.mapbox.scrollZoom.disable();
      this.mapbox.on('wheel', this._wheelEvent);
      this.mapbox.on('dragstart', this._dragStartEvent);
      // Drag events not emited after dragPan disabled, so I use touch event here
      this.mapbox.on('touchstart', this._touchStartEvent);
    }
  }

  private _initZoomEvents(): void {
    const isTouchEvent = (e) => e.originalEvent && 'touches' in e.originalEvent;
    const isTwoFingerTouch = (e) => e.originalEvent.touches.length >= 2;

    this._touchStartEvent = (e) => {
      if (isTouchEvent(e) && !isTwoFingerTouch(e)) this.mapbox.dragPan.disable();
    };
    this._dragStartEvent = (e) => {
      if (isTouchEvent(e) && isTwoFingerTouch(e)) this.mapbox.dragPan.enable();
    };
    this._wheelEvent = (e) => {
      if (e.originalEvent.ctrlKey) {
        // Check if CTRL key is pressed
        e.originalEvent.preventDefault(); // Prevent chrome/firefox default behavior
        if (!this.mapbox.scrollZoom._enabled) this.mapbox.scrollZoom.enable(); // Enable zoom only if it's disabled
      } else if (this.mapbox.scrollZoom._enabled) this.mapbox.scrollZoom.disable(); // Disable zoom only if it's enabled
    };
  }

  private _getMapboxInitConfig(): any {
    return {
      container: this.mapBoxId,
      style: MAPBOX_STYLE,
      center: this._getMapboxCenterByMarkers(),
      zoom: 6.5,
      attributionControl: false,
    };
  }

  private _getMapboxCenterByMarkers(): [number, number] {
    // const points = this.markers.filter((item) => !!item).map((marker) => marker.position);
    // const middlePoint = !!points.length ? this._getMiddlePoint(points) : null;
    // const { lat = 46.886, lng = 8.824 } = middlePoint || this.mapbox?.getCenter() || {};
    // return [ lng, lat ];
    return [8.424, 46.886];
  }

  private _getMiddlePoint(points: IPoint[]): IPoint {
    // count middle point (n0 + n1 + ... nm) / m;
    const addNextPoint = (p: IPoint, c: IPoint, d: number) => ({ lat: p.lat + c.lat / d, lng: p.lng + c.lng / d });
    return points.reduce((prev, curr, i, arr) => addNextPoint(prev, curr, arr.length), { lat: 0, lng: 0 });
  }

  private _addMarkerToMap(marker: IMarker): any {
    if (!this.mapboxApi || !this.mapbox || !marker?.position?.lng || !marker?.position.lat) return null;

    const markerWrapper = document.createElement('div');
    markerWrapper.classList.add('marker');
    const pin = document.createElement('img');
    pin.style.cursor = 'default';
    pin.src = marker.type;
    markerWrapper.appendChild(pin);

    const mapboxMarker = new this.mapboxApi.Marker(markerWrapper)
      .setLngLat([marker.position?.lng, marker.position?.lat])
      .addTo(this.mapbox);

    if (marker.withPopup) {
      const popup = new this.mapboxApi.Popup({ offset: 40 }).setHTML(marker?.popupHtmlContent);
      mapboxMarker.setPopup(popup);
    }

    return mapboxMarker;
  }

  private _updateMarkers(): void {
    this._mapboxMarkers.forEach((marker) => marker?.remove());
    this._mapboxMarkers = this.markers?.map((marker) => this._addMarkerToMap(marker));
    this.mapbox?.setCenter(this._getMapboxCenterByMarkers());
  }
}
